import { useEffect, useState } from "react";

import {
  GoogleTagManagerProviderProps,
  GoogleTagManagerContext,
} from "./GoogleTagManager.context";

// Define interface for window with dataLayer
interface WindowWithDataLayer extends Window {
  dataLayer?: unknown[];
}

export function GoogleTagManagerProvider({
  gtmId,
}: GoogleTagManagerProviderProps) {
  // State to track if GTM has been initialized
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Early return if already initialized, missing GTM ID, or explicitly disabled
    if (isInitialized || !gtmId) {
      return;
    }

    // Create dataLayer array if it doesn't exist
    if (!(window as WindowWithDataLayer).dataLayer) {
      (window as WindowWithDataLayer).dataLayer = [];
    }

    // Create dataLayer array if it doesn't exist
    if (!(window as WindowWithDataLayer).dataLayer) {
      (window as WindowWithDataLayer).dataLayer = [];
    }

    // Check if GTM script is already added
    if (!document.getElementById("google-tag-manager-script")) {
      // Create and inject the GTM script in the head
      const script = document.createElement("script");
      script.id = "google-tag-manager-script";
      script.defer = true; // Use defer to load script after parsing HTML

      // note: modified to use defer attribute for better performance
      script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.defer=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');`;
      document.head.appendChild(script);
    }

    // Check if noscript iframe is already added
    if (!document.getElementById("google-tag-manager-noscript")) {
      // Create and inject the GTM noscript tag for browsers with JavaScript disabled
      const noscript = document.createElement("noscript");
      noscript.id = "google-tag-manager-noscript";
      const iframe = document.createElement("iframe");
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
      iframe.height = "0";
      iframe.width = "0";
      iframe.style.display = "none";
      iframe.style.visibility = "hidden";
      noscript.appendChild(iframe);
      document.body.prepend(noscript);
    }

    // Mark as initialized to prevent duplicate injection
    setIsInitialized(true);
  }, [gtmId, isInitialized]);

  return (
    <GoogleTagManagerContext.Provider
      value={{ dataLayer: (window as WindowWithDataLayer).dataLayer || [] }}
    />
  );
}
